import url from 'url'
import { all, put, call, select, race, take, cancel, fork } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import Toast from 'react-native-toast-message'

// Services
import Config from 'APP/Config'
import ConfigEnv from 'APP/NativeModules/ConfigEnv'
import I18n from 'APP/I18n'
import BranchEventChannel from 'APP/Services/Branch'
import Analytics from 'APP/Services/Analytics'

// Actions
import DeeplinkActions from '../Redux/DeeplinkRedux'
import LoginActions from 'APP/Redux/LoginRedux'
import AccountLinkingActions from 'APP/Redux/AccountLinkingRedux'
import { isWeb, isMobile } from 'APP/Helpers/checkPlatform'
import { stashDestination } from 'APP/Lib/SeamlessDestination'
import { getPrimaryLoginStrategy } from 'APP/Lib/ConfigHelpers'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'

// Types
import { DeeplinkTypes } from 'APP/Redux/DeeplinkRedux'
import { LoginTypes } from 'APP/Redux/LoginRedux'

// Selectors
import { loginState, logout } from './AuthSagas'
import {
  patientProfileState,
  enrolWithEnrolmentCodeDeeplink,
  processFamilyInvite,
} from './PatientSagas'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'

// Helpers
import {
  filterServiceGroupsAndServicesForEligibility,
  getNormalizedServiceGroups,
  TabScreens,
} from 'APP/Lib/ContentHelpers'
import { RootScreens, VerifyEmailErrors } from 'APP/Lib/Enums'

import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import { NESTED_SCREEN_INDEX } from 'APP/Nav'
import moment from 'moment/moment'
import { decodeBase64Token } from 'APP/Lib/Helpers'
import PatientActions, { selectExternalServices } from 'APP/Redux/PatientRedux'
import { clearAllQueryParamsAndHash } from 'APP/Lib/Utilities'
import { logger } from 'APP/Lib/Datadog'

// Simple predicate for paths that should skip startup
export const simplePathCheckPredicate = (path) =>
  ['action/login', 'verifyemail'].some((p) => path?.includes(p) ?? false)

// Signup enrolment code should only skip startup when the user is not logged in (assumed to be a new user)
export const signupEnrolmentCodePredicate = function* (path) {
  const login = yield select(loginState)
  return !login.accessToken && path.includes('signupEnrolmentCode')
}

// Returns the SSO connection for builds that require seamless login (e.g. Sun Life).
// Any build can opt in by adding a LOGIN_STRATEGIES entry with emphasis: 'primary'.
// Returns null for builds with no primary strategy (e.g. standard Dialogue), leaving
// the normal login flow untouched.
export const getPrimaryConnection = () => getPrimaryLoginStrategy()?.connection || null

// Logged-out flows that must NOT be intercepted by seamless SSO: they are designed to run
// without a session (email-change verification, new-user enrolment, family invites) and
// handle authentication themselves.
const SEAMLESS_SSO_EXEMPT_PATHS = ['verifyemail', 'signupEnrolmentCode', 'familyInvite']
export const isSeamlessSSOExemptPath = (path) =>
  SEAMLESS_SSO_EXEMPT_PATHS.some((p) => path?.includes(p) ?? false)

// Stash a deeplink destination so it survives the auth step. On web the SeamlessDestination
// seam persists to sessionStorage (the Auth0 redirect destroys the heap); on native it holds
// in memory (the in-app browser never unloads the app). Consumed by navigateWithMandatoryMfa.
// origin defaults to 'action_login'; the seamless interception passes 'seamless' so only that
// flow counts toward the seamless_sso_success funnel.
const stashPendingSecondAction = (path, props, origin = 'action_login') => {
  stashDestination(path, props, origin)
}

// A primary-SSO deeplink must skip the startup gate so seamless SSO (or reauth) can fire
// immediately. Runs on web and native; embedded is excluded by the interception guard below.
export const connectionLinkPredicate = function* (path) {
  const { enableSeamlessSsoDeeplink } = yield select(selectFeatures)
  if (!enableSeamlessSsoDeeplink || isSeamlessSSOExemptPath(path)) return false
  return !!getPrimaryConnection()
}

// A collection of predicates for deeplink paths that shouldn't wait for startup to complete
// before processing. Made up of functions that take (path, props) and return a boolean.
export const LINKS_THAT_SKIP_READY_TO_PROCESS = [
  simplePathCheckPredicate,
  signupEnrolmentCodePredicate,
  connectionLinkPredicate,
]

export const deeplinkState = (state) => state?.deeplink || {}

const urlSchemes = ConfigEnv.URL_SCHEMES
const regExpTrailingAndLeadingSlashes = /^\/|\/$/g
const regExpUrlScheme = new RegExp(`(${(urlSchemes || []).join('|')})://`, 'gi')

/**
 * Using Share.share() could wrongly encode URL Query Params on some application.
 * This function removed the wrong encoding on the Query Params.
 * See https://dialoguemd.atlassian.net/browse/DIA-45572
 */
const stripEncodedAmpersandKey = (params) => {
  return Object.entries(params).reduce((p, [key, value]) => {
    if (key.startsWith('amp;')) {
      p[key.slice('amp;'.length)] = value
      return p
    }
    p[key] = value
    return p
  }, {})
}

const parseBranchParams = (params) => {
  const { $deeplink_path: deeplinkPath, ...other } = stripEncodedAmpersandKey(params)
  let nonBranchLink = params['+non_branch_link']
  let nonBranchParams = {}
  if (nonBranchLink) {
    nonBranchLink = nonBranchLink.replace(regExpUrlScheme, '')
    const { pathname, query } = url.parse(nonBranchLink, true)
    nonBranchParams = { pathname, query }
  }
  const {
    contentful_service_group_id,
    contentful_service_group_ids,
    contentful_external_service_id: healthResource,
    contentful_external_service_ids,
    id,
    ...rest
  } = { ...other, ...nonBranchParams.query }
  return {
    path: (deeplinkPath || nonBranchParams.pathname || '').replace(
      regExpTrailingAndLeadingSlashes,
      ''
    ),
    props: {
      ...rest,
      id: id || contentful_service_group_id,
      healthResource,
      healthResources: contentful_external_service_ids?.split(','),
      contentful_service_group_id: contentful_service_group_id,
      contentful_service_group_ids: contentful_service_group_ids?.split(','),
    },
  }
}

export function* watchDeeplinkRequests() {
  let waitDeferredLinkResolutionTask
  try {
    const deeplinkChannel = eventChannel(BranchEventChannel)
    // Infinite loop on mobile, as deeplinks can be received at any time in the process lifetime whereas on web it's only at startup
    do {
      const { error, params } = yield take(deeplinkChannel)
      if (error) {
        logger.error('Failed to process deeplink', {}, error)
        continue
      }
      // Link is for universal login, and should not be processed
      if (params['+non_branch_link']?.startsWith?.(Config.AUTH0_URL)) {
        continue
      }

      // Handle the data...
      const { path, props } = parseBranchParams(params)

      yield call(Analytics.trackUtmParameters, {
        type: 'branchIO',
        args: { path, props },
      })

      if (props && props['~referring_link']) {
        Analytics.trackEvent('deep_link_opened', { trigger: path, ...props })
      }

      // Aliased channelId as 'chat' for member-facing deeplinks to not expose internal MM concept
      if (props?.chat) {
        props.channelId = props.chat
      }

      if (path || props.type) {
        yield put(DeeplinkActions.setDeferredDeeplink(path, props))

        // Check each special case predicate to see if we should skip the readyToProcess check (triggered at the end of startup)
        // and process the deeplink immediately. If any of the predicates return true the deeplink will be processed immediately.
        const shouldSkipReadyToProcess = (yield all(
          LINKS_THAT_SKIP_READY_TO_PROCESS.map((predicate) => call(predicate, path, props))
        )).some(Boolean)

        if (shouldSkipReadyToProcess) {
          yield put(DeeplinkActions.processDeferredDeeplink())
        } else {
          const { readyToProcess } = yield select(deeplinkState)

          if (readyToProcess) {
            yield put(DeeplinkActions.processDeferredDeeplink())
          } else {
            // Rely on the last step of startup nav to trigger deeplink processing
            // This watcher is only needed till we define the UX for an interrupted flow (e.g. incomplete profile, logged out)
            if (waitDeferredLinkResolutionTask) {
              yield cancel(waitDeferredLinkResolutionTask)
            }
            waitDeferredLinkResolutionTask = yield fork(waitDeferredLinkResolution)
          }
        }
      }
    } while (isMobile())
  } catch (error) {
    logger.error('Fatal error while processing deeplink requests', {}, error)
  }
}

export function* rejectDeferredDeeplink(reasonProp, path, props, ...other) {
  let reason = reasonProp
  if (!reason) {
    const login = yield select(loginState)
    reason = !login.accessToken ? 'unauthorized' : 'incompleteProfile'
  }
  yield put(DeeplinkActions.rejectDeferredDeeplink(reason, path, props, ...other))
  Analytics.trackEvent('deep_link_rejected', {
    reason,
    trigger: path || null,
    id: props?.id || null,
    services: props?.services || null,
    $marketing_title: props?.['$marketing_title'],
    '~referring_link': props?.['~referring_link'],
  })
  Toast.show({
    text1: I18n.t(`Deeplinks.error.title`),
    text2: I18n.t(`Deeplinks.error.${reason}`),
  })
}

export function* waitDeferredLinkResolution() {
  const { logout } = yield race({
    logout: take(LoginTypes.LOGOUT),
    ready: take(({ type, ready }) => type === DeeplinkTypes.SET_READY_TO_PROCESS && ready),
  })
  if (logout) {
    yield call(rejectDeferredDeeplink)
  }
}

export function* verifyNewEmailDeeplink({ token: rowToken }) {
  const tokenValues = decodeBase64Token(rowToken)
  const { expiration_date, new_email, old_email, token } = tokenValues

  const isTokenExpired = moment(expiration_date).diff(moment()) < 0

  const isTokenValid = new_email && old_email && token && expiration_date

  yield put(PatientActions.setChangeEmailToken(tokenValues))

  if (isTokenExpired || !isTokenValid) {
    const errorType = isTokenExpired
      ? VerifyEmailErrors.TOKEN_EXPIRED
      : VerifyEmailErrors.TOKEN_INVALID
    yield call(NestedNavHelper.navigate, 'verifyNewEmailResult', { errorType })
  } else {
    yield call(NestedNavHelper.navigate, 'verifyNewEmail')
  }
}

export function* processDeferredDeeplink() {
  try {
    const { path, props, expiry } = yield select(deeplinkState)
    const { eligibleServices } = yield select(patientProfileState)
    const healthResources = yield select(selectExternalServices)
    const { accessToken } = yield select(loginState)
    const isStillValid = Date.now() < Date.parse(expiry)

    if (!isStillValid) {
      Analytics.trackEvent('deep_link_expired', { trigger: path, ...props })
      yield put(DeeplinkActions.processDeferredDeeplinkExpired())

      return
    }

    if (props?.type) {
      switch (props.type) {
        default:
          logger.error('Unknown deeplink type passed', { deeplinkType: props.type })
          break
      }
    } else if (path) {
      // When a deeplink is picked up by the watchDeeplinkRequests saga, the path is trimmed. When a second_action is processed, we skip this step. This ensures that all paths are in the same format.
      const trimmedPath = path.replace(regExpTrailingAndLeadingSlashes, '')
      const isAction = trimmedPath.split('/')[0] === 'action'

      // Seamless SSO: a member on an SSO build (e.g. Sun Life) must be signed in through the
      // build's configured IdP before landing on <path>, including when Dialogue already holds
      // a session for a different user (e.g. My SL switched accounts). The connection is derived
      // from LOGIN_STRATEGIES rather than carried on the link. Runs on web and native; embedded
      // (OneApp) is excluded because it authenticates through its host app. Logged-out-capable
      // flows (verifyemail, signupEnrolmentCode, familyInvite) keep their own handling below.
      const primaryConnection = getPrimaryConnection()
      const seamlessSsoCandidate =
        !isEmbeddedEnvironment() &&
        !isAction &&
        primaryConnection &&
        !isSeamlessSSOExemptPath(trimmedPath)
      // Only read the LD flag once the cheap sync gates pass, so unrelated deeplinks don't
      // select it (keeps this off the hot path and out of their effect sequence).
      const { enableSeamlessSsoDeeplink } = seamlessSsoCandidate ? yield select(selectFeatures) : {}
      if (seamlessSsoCandidate && enableSeamlessSsoDeeplink) {
        // Funnel start (Datadog, real-time) — pairs with seamless_sso_success for completion rate.
        logger.info('seamless_sso_started', { connection: primaryConnection })
        yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
        yield put(AccountLinkingActions.resetAccountLinking())
        // Stash the destination so the member lands here after SSO instead of the home screen.
        // Logged-out: WelcomeScreen reads the connection and triggers the silent Auth0 redirect.
        // Logged-in during startup: full Auth0 logout so the IdP can re-bind; silent login
        // runs on return in fullStartup (not here). PatientSagas then consumes the stash.
        // Props are stashed too — without them the destination screen loads without the data it needs.
        // Tagged 'seamless' so only this flow counts toward the success funnel.
        clearAllQueryParamsAndHash()
        yield put(LoginActions.setConnection(primaryConnection))
        const { readyToProcess } = yield select(deeplinkState)
        const arrivedDuringStartup = !readyToProcess
        if (accessToken && arrivedDuringStartup) {
          logger.info('seamless_sso_reauth_started', {
            connection: primaryConnection,
            path: trimmedPath,
          })

          // reauthPath/reauthProps are extra fields not on the LoginActions.logout() action
          // creator — they're only read when logout is called directly via yield call() from
          // this saga. Any put(LoginActions.logout(...)) path ignores them safely.
          yield call(logout, {
            reason: 'seamless_sso_account_switch',
            reauthPath: trimmedPath,
            reauthProps: props,
          })
        } else {
          stashPendingSecondAction(trimmedPath, props, 'seamless')
        }
        return
      }

      if (isAction) {
        const action = trimmedPath.split('/')[1]

        switch (action) {
          case 'login': {
            const { connection, second_action } = props
            if (!connection) break
            yield put(DeeplinkActions.processDeferredDeeplinkSuccess())

            if (accessToken) {
              if (isWeb()) {
                // User is already logged in — let startup finish before navigating.
                // Navigating immediately races with the startup nav flow and gets overridden.
                if (second_action) {
                  const { readyToProcess } = yield select(deeplinkState)
                  if (readyToProcess) {
                    // Startup already completed (e.g. Branch arrived late due to
                    // ad-blocker/CDN delay) — navigateWithMandatoryMfa has run and would
                    // never consume a sessionStorage stash, so navigate directly.
                    yield put(DeeplinkActions.setDeferredDeeplink(second_action, props || {}))
                    yield put(DeeplinkActions.processDeferredDeeplink())
                  } else {
                    // Save second_action for navigateWithMandatoryMfa to pick up at the
                    // end of startup, where it is consumed and removed.
                    stashPendingSecondAction(second_action, props)
                  }
                }
                return
              } else {
                yield call(logout, { reason: 'Log out before log in deeplink' })
              }
            }

            yield put(AccountLinkingActions.resetAccountLinking())
            yield put(LoginActions.setConnection(connection))
            // To stop infinite loop of processing SSO deeplink branch
            clearAllQueryParamsAndHash()
            // Note: we used to explicitly attempt to trigger the login sequence here.
            // This was poor UX, because browsers tend to block pop-ups that are
            // programatically triggered.
            if (second_action) {
              if (isWeb()) {
                stashPendingSecondAction(second_action, props)
              } else {
                yield put(DeeplinkActions.setDeferredDeeplink(second_action, props || {}))
              }
            }
            return
          }
          default:
            break
        }
      } else if (path === 'signupEnrolmentCode') {
        // Special case for enrolment code when logged in, behaves more like an action than just navigation
        if (accessToken && props?.code) {
          yield call(enrolWithEnrolmentCodeDeeplink, props?.code)
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
        } else if (!accessToken) {
          yield put(LoginActions.signupRequest())
        }
        return
      } else if (path === 'familyInvite') {
        // Special case to accept family invite, behaves more like an action than just navigation
        if (accessToken && props?.family_id) {
          yield call(processFamilyInvite, props?.family_id)
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
        } else if (!accessToken) {
          // If the member does not have an access token, they will need to create an account and will be
          // provisioned already in the family, so we can just navigate normally
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
        }
        return
      } else if (path === 'navigateToIcbt') {
        // ⚠️ This is brittle and could change, breaking this deeplink ⚠️
        const hasIcbtService = !!(
          eligibleServices['icbt_guided'] || eligibleServices['icbt_self_serve']
        )

        if (!hasIcbtService) {
          yield call(rejectDeferredDeeplink, 'notEligible', path, props)
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
          return
        }

        yield call(NestedNavHelper.navigate, RootScreens?.mind, {
          tabName: TabScreens?.mind?.toolkits,
        })
        yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
        return
      } else if (path === 'verifyemail') {
        yield call(verifyNewEmailDeeplink, props)
        return
      } else if (path.includes('android') && path.includes('/callback')) {
        // During the login process, android redirects back to the app via
        // /android/{application_id}/callback (see AndroidManifest.xml)
        // this is an expected path, and does not require processing,
        return
      } else {
        // If service passed, confirm user is eligible
        const { services } = props || {}
        const servicesSplit = services ? services.split(',') : []

        const userDoesNotHaveAccess = services
          ? servicesSplit.filter((p) => eligibleServices[p]).length < servicesSplit.length
          : false

        const healthResourceIds = [
          ...(props?.healthResources || []),
          ...(props?.healthResource ? [props.healthResource] : []),
        ]

        let healthResourceId = null

        // Check if item_id is an eligible health resource
        const itemIdNotInEligibleServices =
          healthResourceIds.length > 0 &&
          !healthResources.some((resource) => {
            const attrs = resource.attributes
            if (!attrs) return false

            const contentIds = [
              ...(attrs.cmsContentId ? [attrs.cmsContentId] : []),
              ...(attrs.cmsContent ? attrs.cmsContent.map((content) => content.cmsContentId) : []),
            ]

            for (const id of contentIds) {
              if (healthResourceIds.includes(id)) {
                healthResourceId ??= id
                return true
              }
            }

            return false
          })

        // Check if user is eligible for any of the reference service groups
        const serviceGroupIds = [
          ...(props?.contentful_service_group_ids || []),
          ...(props?.contentful_service_group_id ? [props.contentful_service_group_id] : []),
        ]
        const normalizedServiceGroups = yield call(
          getNormalizedServiceGroups,
          serviceGroupIds,
          I18n.locale
        )
        const filteredServiceGroups = filterServiceGroupsAndServicesForEligibility(
          normalizedServiceGroups,
          eligibleServices,
          healthResources
        )
        const noAccessToServiceGroups = serviceGroupIds.length && !filteredServiceGroups.length

        // if the user does not have access to the provided internal service(s), health resource(s),
        // or app service group(s), reject the deeplink
        if (userDoesNotHaveAccess || itemIdNotInEligibleServices || noAccessToServiceGroups) {
          yield call(rejectDeferredDeeplink, 'notEligible', path, props)
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
          return
        }

        const routeProps = props && {
          ...props,
          ...(props.healthResources && healthResourceId
            ? { healthResource: healthResourceId }
            : {}),
        }

        // if multiple eligible service groups to link to, deeplink to the first one
        if (filteredServiceGroups.length) {
          routeProps['id'] = filteredServiceGroups[0].id
        }

        const routeExists = NestedNavHelper.getRouteNames().indexOf(path) !== -1
        if (routeExists) {
          if (isEmbeddedEnvironment()) {
            const isNestedTab = !!NESTED_SCREEN_INDEX[path]
            if (isNestedTab) {
              yield call(NestedNavHelper.reset, {
                index: 0,
                routes: [{ name: path, params: routeProps }],
              })
            } else {
              yield call(NestedNavHelper.reset, {
                index: 1,
                routes: [{ name: 'tabbar' }, { name: path, params: routeProps }],
              })
            }
          } else {
            yield call(NestedNavHelper.navigate, path, routeProps)
          }
          yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
          return
        }
      }
    }

    yield call(rejectDeferredDeeplink, 'invalidLink', path, props)
    yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
  } catch (error) {
    yield put(DeeplinkActions.processDeferredDeeplinkFailure(error.message))
  }
}
